// ---- Reads ---------------------------------------------------------------

import { useQuery, useQueries, keepPreviousData } from '@tanstack/react-query';
import {
  serviceApi,
  onPath,
  onKeys,
  unwrap,
  getPollingInterval,
  type WaitingState,
} from '../_shared';
import type {
  Instance,
  InstanceSummary,
  MonitoringPoint,
  MonitoringQuery,
  RemoteTicket,
} from './instance-types';

const SETTLED_TERMINAL = ['POWERED_OFF', 'OFF', 'INACTIVE', 'SUSPENDED', 'ERROR', 'FAILED', 'TERMINATED'];
const SETTLED_RUNNING = ['POWERED_ON', 'ACTIVE', 'RUNNING'];

/**
 * Whether an instance has finished moving: it reached a resting state, or it is
 * running *with* networking attached. The second half matters right after a
 * create — a VM reports POWERED_ON before its NIC appears, and stopping there
 * leaves the detail page showing no network.
 *
 * Nothing to look at yet counts as settled, so an empty cache never starts a
 * poll of its own.
 */
export function instanceHasSettled(inst: Instance | undefined): boolean {
  if (!inst) return true;
  const key = String(
    inst.state === 'BACKUP' || inst.state === 'BACKUP_POWEROFF' ? inst.state : inst.status ?? '',
  ).toUpperCase();
  if (SETTLED_TERMINAL.includes(key)) return true;
  const hasIp = (inst.metadata?.nics ?? []).some((n) => n.ip);
  return SETTLED_RUNNING.includes(key) && hasIp;
}

export function useInstanceList(serviceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.instances(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async () => {
      const body = unwrap<{ instances?: Instance[] } | Instance[]>(
        await serviceApi.get(onPath(serviceId!, 'instances')),
      );
      return Array.isArray(body) ? body : body.instances ?? [];
    },
  });
}

export function useInstanceSummary(serviceId: string | undefined, instanceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.instanceSummary(serviceId ?? '', instanceId ?? ''),
    enabled: !!serviceId && !!instanceId,
    retry: false,
    refetchInterval: 15_000,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const body = unwrap<{ instance?: InstanceSummary }>(
        await serviceApi.get(onPath(serviceId!, `instances/${instanceId}/summary`)),
      );
      return body.instance ?? {};
    },
  });
}

/**
 * Batch summary fetch for a list page. Fires one query per instance id but all
 * under the same `useQueries` call so React can batch the renders. Returns a
 * stable map keyed by instance id so list cells never trigger individual fetches.
 * React-Query deduplicates against any matching `instanceSummary` keys already
 * in the cache (e.g. from the detail page).
 */
export function useInstanceSummaries(
  serviceId: string | undefined,
  instanceIds: string[],
): Record<string, InstanceSummary> {
  const results = useQueries({
    queries: instanceIds.map((id) => ({
      queryKey: onKeys.instanceSummary(serviceId ?? '', id),
      enabled: !!serviceId && !!id,
      retry: false,
      refetchInterval: 15_000,
      placeholderData: keepPreviousData,
      queryFn: async () => {
        const body = unwrap<{ instance?: InstanceSummary }>(
          await serviceApi.get(onPath(serviceId!, `instances/${id}/summary`)),
        );
        return body.instance ?? {};
      },
    })),
  });

  // Collapse results into a stable map: id → summary (or empty object while loading).
  const map: Record<string, InstanceSummary> = {};
  instanceIds.forEach((id, i) => {
    map[id] = results[i].data ?? {};
  });
  return map;
}

/** Detail with adaptive polling: pass the context `isWaitingUpdate` to speed up
 *  refetches while a dispatched action settles. */
export function useInstanceDetail(
  serviceId: string | undefined,
  instanceId: string | undefined,
  isWaitingUpdate: WaitingState = false,
) {
  return useQuery({
    queryKey: onKeys.instance(serviceId ?? '', instanceId ?? ''),
    enabled: !!serviceId && !!instanceId,
    retry: false,
    refetchInterval: (query) => {
      // A user-dispatched action (reboot/resize/…) sets the waiting flag → use it.
      if (isWaitingUpdate) return getPollingInterval(isWaitingUpdate);
      // Otherwise keep polling a freshly-created / still-booting instance (nothing
      // set the flag) until it settles WITH networking attached, so the detail page
      // doesn't stay stuck on PENDING with no NICs right after create.
      return instanceHasSettled(query.state.data as Instance | undefined) ? false : 5000;
    },
    queryFn: async () => {
      const body = unwrap<{ instance?: Instance } | Instance>(
        await serviceApi.get(onPath(serviceId!, `instances/${instanceId}`)),
      );
      return (body && typeof body === 'object' && 'instance' in body ? body.instance : body) as Instance;
    },
  });
}

export function useInstanceMonitoring(
  serviceId: string | undefined,
  instanceId: string | undefined,
  query?: MonitoringQuery,
) {
  return useQuery({
    queryKey: onKeys.instanceMonitoring(serviceId ?? '', instanceId ?? '', query as Record<string, unknown> | undefined),
    enabled: !!serviceId && !!instanceId,
    retry: false,
    refetchInterval: 15_000,
    queryFn: async () => {
      const body = unwrap<{ monitoring?: MonitoringPoint[] } | MonitoringPoint[]>(
        await serviceApi.get(onPath(serviceId!, `instances/${instanceId}/monitoring`), {
          params: query,
        }),
      );
      return Array.isArray(body) ? body : body.monitoring ?? [];
    },
  });
}

export function useInstanceRemoteTicket(
  serviceId: string | undefined,
  instanceId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: onKeys.instanceConsole(serviceId ?? '', instanceId ?? ''),
    enabled: enabled && !!serviceId && !!instanceId,
    retry: false,
    queryFn: async () =>
      unwrap<RemoteTicket>(await serviceApi.get(onPath(serviceId!, `instances/${instanceId}/vnc`))),
  });
}
